/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { CopyCTA, Grid } from '@livecontrol/core-ui';
import { Icons } from '@livecontrol/scheduler/components';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Panel } from '../../../../components';
import { DestinationType } from '../../../../graphql-types';
import type { Destination, Maybe } from '../../../../graphql-types';

const Logo = styled.span`
  width: min-content;
  height: min-content;
  border: 1px solid var(--white10);
  padding: 8px;
  background: var(--white5);
  font-size: 24px;
`;

const Body = styled.div`
  border-top: 1px solid var(--white40);
  margin: 0 24px;
  padding: 24px;
`;

export const Card = ({
  destination
}: {
  destination: Maybe<Partial<Destination>>;
}): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isExpandable = useMemo(
    () =>
      destination?.type !== 'LiveControl' &&
      !!(
        destination?.destinationPlayerUrl ||
        destination?.streamKey ||
        destination?.streamPasstrough
      ),
    [destination]
  );

  return (
    <Panel className='mt-8px p-0 bg-white5'>
      <button
        type='button'
        className='x-pseudo w-100 p-16px'
        disabled={!isExpandable}
        onClick={(): void => {
          setIsExpanded(!isExpanded);
        }}
      >
        <Grid className='px-24px' gridTemplateColumns='4rem 1fr 10rem 3rem' alignItems='center'>
          {destination?.type === 'LiveControl' ? (
            <Logo className='rounded-circle'>
              <Icons.LcIcon color='white' size='27' />
            </Logo>
          ) : destination?.type === 'Facebook' ? (
            <Logo className='rounded-circle' style={{ color: '#285def' }}>
              <i className='fab fa-facebook' />
            </Logo>
          ) : destination?.type === 'YouTube' ? (
            <Logo className='rounded-circle' style={{ color: '#f50004' }}>
              <i className='fab fa-youtube' />
            </Logo>
          ) : (
            <Logo className='rounded-circle text-white'>
              <i className='fas fa-signal-stream' />
            </Logo>
          )}
          {destination?.destinationPlayerUrl ? (
            <a
              className='text-white font-weight-bold text-left m-0'
              href={destination.destinationPlayerUrl}
              target='_blank'
              rel='noreferrer'
              onClick={(e): void => {
                e.stopPropagation();
              }}
            >
              {destination.name} <i className='far fa-external-link ml-4px' />
            </a>
          ) : (
            <p className='text-white font-weight-bold text-left m-0'>{destination?.name}</p>
          )}
          {destination?.type !== DestinationType.Rtmp && (
            <p className='text-white text-left m-0'>
              {destination?.status === 'Streaming' ? (
                <span className='text-danger mr-4px'>
                  <i className='fas fa-circle' />
                </span>
              ) : (
                <span className='text-white40 mr-4px'>
                  <i className='fas fa-circle' />
                </span>
              )}
              {destination?.status}
            </p>
          )}
          {destination?.type === DestinationType.Rtmp && (
            <p className='text-white text-left m-0' /> // Blank space to align chevron
          )}
          {isExpandable && isExpanded ? (
            <span className='text-white'>
              <i className='fas fa-chevron-up' />
            </span>
          ) : isExpandable ? (
            <span className='text-white'>
              <i className='fas fa-chevron-down' />
            </span>
          ) : undefined}
        </Grid>
      </button>
      {isExpanded && (
        <Body>
          {destination?.name && (
            <div className='text-white mb-8px'>
              <CopyCTA noIcon toastText='Stream Name Copied' value={destination.name}>
                <span className='font-weight-bold'>Stream Name:</span> {destination.name}{' '}
                <i className='fas fa-copy' />
              </CopyCTA>
            </div>
          )}
          {destination?.streamUrl && (
            <div className='text-white mb-8px'>
              <CopyCTA noIcon toastText='Stream URL Copied' value={destination.streamUrl}>
                <span className='font-weight-bold'>Stream URL:</span> {destination.streamUrl}{' '}
                <i className='fas fa-copy' />
              </CopyCTA>
            </div>
          )}
          {destination?.streamKey && (
            <div className='text-white mb-8px'>
              <CopyCTA noIcon toastText='Stream Key Copied' value={destination.streamKey}>
                <span className='font-weight-bold'>Stream Key:</span> {destination.streamKey}{' '}
                <i className='fas fa-copy' />
              </CopyCTA>
            </div>
          )}
          {destination?.streamPasstrough && (
            <div className='text-white mb-8px'>
              <CopyCTA
                noIcon
                toastText='Stream Passthrough Copied'
                value={destination.streamPasstrough}
              >
                <span className='font-weight-bold'>Stream Passthrough:</span>{' '}
                {destination.streamPasstrough} <i className='fas fa-copy' />
              </CopyCTA>
            </div>
          )}
        </Body>
      )}
    </Panel>
  );
};
